/**
 * UserMenu — avatar + dropdown for the authenticated user.
 *
 * Ported from QYAS Analytics. Replaces QYAS's Redux-based dispatch
 * with qfinpricer's `useAuth()` hook. Drops the theme-toggle entry
 * (qfinpricer is dark-only). Replaces MUI icons with lucide-react.
 *
 * Spec: 2026-05-19-qfinpricer-portal-skin-qyas.md (Day 1, item 1)
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  Box,
} from '@mui/material';
import { LogOut as LogOutIcon, User as UserIcon, Settings as SettingsIcon } from 'lucide-react';
import { useAuth } from '../../auth/AuthProvider';

export const UserMenu: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const open = Boolean(anchorEl);

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    handleClose();
    try {
      await logout();
    } finally {
      navigate('/login', { replace: true });
    }
  };

  const initial = (user?.username?.[0] ?? 'U').toUpperCase();

  return (
    <>
      <IconButton onClick={handleOpen} size="small" sx={{ ml: 1 }} aria-label="Open user menu">
        <Avatar
          sx={{
            width: 30,
            height: 30,
            bgcolor: 'action.hover',
            color: 'primary.main',
            fontSize: '0.85rem',
            border: '1px solid',
            borderColor: 'primary.main',
          }}
        >
          {initial}
        </Avatar>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Box sx={{ px: 2, py: 1, minWidth: 200 }}>
          <Typography variant="subtitle2" fontWeight={600}>
            {user?.username ?? 'Account'}
          </Typography>
          {user?.role && (
            <Typography variant="caption" color="text.secondary">
              {user.role}
            </Typography>
          )}
        </Box>
        <Divider />

        <MenuItem onClick={() => { navigate('/profile'); handleClose(); }}>
          <ListItemIcon><UserIcon size={16} /></ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={() => { navigate('/settings'); handleClose(); }}>
          <ListItemIcon><SettingsIcon size={16} /></ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <Divider />

        <MenuItem onClick={handleLogout}>
          <ListItemIcon><LogOutIcon size={16} /></ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
};

export default UserMenu;
